/**
 * @description
 * Domain trends panel component showing growth/decline indicators and sparklines.
 * Displays trend analysis with visual indicators and sorting options.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/DomainTrendsPanel.test.tsx
 */

import { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, ArrowUpDown } from 'lucide-react';
import type { DomainTrend } from '../../../types/vscode-lens';
import { formatDomain } from '../../utils/domainUtils';

interface DomainTrendsPanelProps {
  /** Array of domain trends */
  trends: DomainTrend[];
  
  /** Time range for the trends */
  timeRange: { start: number; end: number };
  
  /** Comparison period label */
  comparisonPeriod?: 'previous' | 'week_ago' | 'month_ago';
  
  /** Initial sort option */
  sortBy?: 'growth' | 'decline' | 'total' | 'alphabetical';
  
  /** Callback when domain is selected */
  onDomainSelect?: (domain: string) => void;
}

type SortOption = 'growth' | 'decline' | 'total' | 'alphabetical';
type FilterOption = 'all' | 'up' | 'down' | 'stable';

/**
 * Domain trends panel with sorting and filtering
 */
export function DomainTrendsPanel({
  trends,
  timeRange: _timeRange,
  comparisonPeriod = 'previous',
  sortBy = 'growth',
  onDomainSelect,
}: DomainTrendsPanelProps) {
  const [currentSort, setCurrentSort] = useState<SortOption>(sortBy);
  const [filter, setFilter] = useState<FilterOption>('all');

  // Get comparison period label
  const comparisonLabel = useMemo(() => {
    switch (comparisonPeriod) {
      case 'week_ago':
        return 'vs. last week';
      case 'month_ago':
        return 'vs. last month';
      default:
        return 'vs. previous period';
    }
  }, [comparisonPeriod]);

  // Filter and sort trends
  const displayTrends = useMemo(() => {
    let filtered = trends;

    // Apply filter
    if (filter !== 'all') {
      filtered = trends.filter(t => t.trend === filter);
    }

    // Apply sort
    const sorted = [...filtered];
    switch (currentSort) {
      case 'growth':
        sorted.sort((a, b) => b.percentageChange - a.percentageChange);
        break;
      case 'decline':
        sorted.sort((a, b) => a.percentageChange - b.percentageChange);
        break;
      case 'total':
        sorted.sort((a, b) => b.currentPeriod - a.currentPeriod);
        break;
      case 'alphabetical':
        sorted.sort((a, b) => a.domain.localeCompare(b.domain));
        break;
    }

    return sorted;
  }, [trends, filter, currentSort]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const growing = trends.filter(t => t.trend === 'up').length;
    const declining = trends.filter(t => t.trend === 'down').length;
    const stable = trends.filter(t => t.trend === 'stable').length;
    const avgChange = trends.reduce((sum, t) => sum + t.percentageChange, 0) / trends.length;

    return { growing, declining, stable, avgChange };
  }, [trends]);

  // Render trend icon
  const renderTrendIcon = (trend: DomainTrend) => {
    if (trend.trend === 'up') {
      return <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />;
    } else if (trend.trend === 'down') {
      return <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />;
    } else {
      return <Minus className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  // Render percentage badge
  const renderPercentageBadge = (trend: DomainTrend) => {
    const isPositive = trend.percentageChange > 0;
    const isNeutral = Math.abs(trend.percentageChange) < 5;

    const colorClass = isNeutral
      ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
      : isPositive
      ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300'
      : 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300';

    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium ${colorClass}`}>
        {isPositive && '+'}
        {trend.percentageChange.toFixed(1)}%
      </span>
    );
  };

  // Render mini sparkline
  const renderSparkline = (trend: DomainTrend) => {
    const points = [trend.previousPeriod, trend.currentPeriod];
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = max - min || 1;

    const heights = points.map(p => ((p - min) / range) * 100);

    return (
      <div className="flex items-end gap-0.5 h-8 w-16">
        {heights.map((height, i) => (
          <div
            key={i}
            className={`flex-1 rounded-t ${
              trend.trend === 'up'
                ? 'bg-green-500'
                : trend.trend === 'down'
                ? 'bg-red-500'
                : 'bg-gray-500'
            }`}
            style={{ height: `${Math.max(height, 10)}%` }}
          />
        ))}
      </div>
    );
  };

  if (trends.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-6 text-center text-muted-foreground">
        <p>No trend data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Domain Trends</h3>
            <p className="text-sm text-muted-foreground">{comparisonLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
            <select
              value={currentSort}
              onChange={(e) => setCurrentSort(e.target.value as SortOption)}
              className="text-sm border rounded-md px-2 py-1 bg-background"
            >
              <option value="growth">Highest Growth</option>
              <option value="decline">Highest Decline</option>
              <option value="total">Total Activity</option>
              <option value="alphabetical">Alphabetical</option>
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.growing}
            </div>
            <div className="text-xs text-muted-foreground">Growing</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats.declining}
            </div>
            <div className="text-xs text-muted-foreground">Declining</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {stats.stable}
            </div>
            <div className="text-xs text-muted-foreground">Stable</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">
              {stats.avgChange > 0 && '+'}
              {stats.avgChange.toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">Avg Change</div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex border-b">
        {(['all', 'up', 'down', 'stable'] as FilterOption[]).map((option) => (
          <button
            key={option}
            onClick={() => setFilter(option)}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              filter === option
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option === 'all' ? 'All' : option === 'up' ? 'Growing' : option === 'down' ? 'Declining' : 'Stable'}
            <span className="ml-2 text-xs">
              ({option === 'all' ? trends.length : trends.filter(t => t.trend === option).length})
            </span>
          </button>
        ))}
      </div>

      {/* Trends List */}
      <div className="divide-y max-h-96 overflow-y-auto">
        {displayTrends.map((trend) => (
          <button
            key={trend.domain}
            onClick={() => onDomainSelect?.(trend.domain)}
            className="w-full p-4 hover:bg-accent/50 transition-colors text-left"
          >
            <div className="flex items-center justify-between gap-4">
              {/* Domain Info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {renderTrendIcon(trend)}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" title={trend.domain}>
                    {formatDomain(trend.domain, 30)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {trend.previousPeriod.toLocaleString()} → {trend.currentPeriod.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Sparkline */}
              <div className="hidden sm:block">
                {renderSparkline(trend)}
              </div>

              {/* Percentage Badge */}
              <div>
                {renderPercentageBadge(trend)}
              </div>

              {/* Confidence Indicator */}
              {trend.confidence !== undefined && (
                <div className="hidden md:block">
                  <div className="text-xs text-muted-foreground">
                    {(trend.confidence * 100).toFixed(0)}% conf.
                  </div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Empty State */}
      {displayTrends.length === 0 && (
        <div className="p-8 text-center text-muted-foreground">
          <p>No {filter !== 'all' ? filter : ''} trends found</p>
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */